import React, { createContext, useContext, useEffect } from "react";

export type ThemeMode = "light" | "dark";

interface ThemeModeContextValue {
	mode: ThemeMode;
	setMode: (mode: ThemeMode) => void;
	toggleMode: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(
	undefined
);

export const ThemeModeProvider: React.FC<React.PropsWithChildren> = ({
	children
}) => {
	const [mode, setMode] = React.useState<ThemeMode>(() => {
		if (typeof window === "undefined") {
			return "light";
		}
		return (
			(window.localStorage.getItem(
				"fashionbazi-theme"
			) as ThemeMode | null) ?? "light"
		);
	});

	useEffect(() => {
		if (typeof window !== "undefined") {
			window.localStorage.setItem("fashionbazi-theme", mode);
		}
	}, [mode]);

	const toggleMode = () => {
		setMode((current) => (current === "light" ? "dark" : "light"));
	};

	return (
		<ThemeModeContext.Provider value={{ mode, setMode, toggleMode }}>
			{children}
		</ThemeModeContext.Provider>
	);
};

export const useThemeMode = () => {
	const context = useContext(ThemeModeContext);
	if (!context) {
		throw new Error("useThemeMode must be used within ThemeModeProvider");
	}
	return context;
};

export default ThemeModeContext;
